import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import AppConfig from "config/AppConfig";
import StorageHelper from "utilities/StorageHelper";
import httpRequest from "../../services/httpRequest";
import AppUtil from "../../utilities/AppUtil";

// Main URL Endpoint
const _urlPrefix = "/auth";

export const login = createAsyncThunk(
  "auth/login",
  async (params, { dispatch }) => {
    const result = await httpRequest({
      url: _urlPrefix + "/login",
      method: "POST",
      data: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    if (params.keepMeLogin) {
      StorageHelper.setCookie(AppConfig.STORAGE_KEYS.token, result.data.accessToken, {
        maxAge: 24 * 60 * 60,
        // maxAge: 2 * 60 * 60,
      });
    } else {
      StorageHelper.setCookie(AppConfig.STORAGE_KEYS.token, result.data.accessToken, {
        maxAge: 24 * 60 * 60,
        // maxAge: 60 * 60,
      });
    }
    await dispatch(getMe());
    return result.data;
  }
);

export const socialLogin = createAsyncThunk(
  "auth/socialLogin",
  async ({ data, type }, { dispatch }) => {
    let url = _urlPrefix + "/facebook-login";
    if (type === AppConfig.SOCIAL_LOGIN_TYPES.GOOGLE) {
      url = _urlPrefix + "/google-login";
    } else if (type === AppConfig.SOCIAL_LOGIN_TYPES.APPLE) {
      url = _urlPrefix + "/apple-login";
    }
    const result = await httpRequest({
      url: url,
      method: "POST",
      data: AppUtil.toSnakeCaseKey(data),
    });
    if (!result) {
      return;
    }
    StorageHelper.setCookie(AppConfig.STORAGE_KEYS.token, result.data.accessToken, {
      maxAge: 24 * 60 * 60,
      // maxAge: 60 * 60,
    });
    dispatch(getMe());
    return result.data;
  }
);

// Logout
export const logout = createAsyncThunk(
  "auth/logout",
  async () => {
    const result = await httpRequest({
      url: `${_urlPrefix}/logout`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    await StorageHelper.removeCookie(AppConfig.STORAGE_KEYS.token);
    StorageHelper.removeLocalItem(AppConfig.STORAGE_KEYS.user);

    return result.data;
  }
);

export const getMe = createAsyncThunk(
  "auth/getMe",
  async () => {
    const result = await httpRequest({
      url: `${_urlPrefix}/info`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    StorageHelper.setLocalObject(AppConfig.STORAGE_KEYS.user, result.data);
    return result.data;
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix + "/forgot-password",
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);
export const changeEmail = createAsyncThunk(
  "auth/changeEmail",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/change-email`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/change-password`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchAccountList = createAsyncThunk(
  "auth/fetchAccountList",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/account-list`,
      method: 'GET',
      params: AppUtil.toSnakeCaseKey(params)
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);


const _isAuthenticated = !!StorageHelper.getCookie(
  AppConfig.STORAGE_KEYS.token
);

const initialState = {
  isAuthenticated: _isAuthenticated,
  isSessionValidating: _isAuthenticated,
  user: StorageHelper.getLocalObject(AppConfig.STORAGE_KEYS.user),
  userInfoSidebar: {
    firstName: '',
    lastName: '',
    organizationName: ''
  } 
  // || {
  //   approvalStatus: "PENDING",
  //   authId: 22,
  //   email: "worker@careaid.com",
  //   expireDate: "2021/04/29 08:11:04",
  //   firstName: "Worker",
  //   lastName: "One",
  //   systemRole: "SUPPORT_WORKER"
  // },
};

const session = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    saveAuthToken: (auth, action) => {
      const { accessToken, expireDate } = action.payload;
      console.log(expireDate);
      auth.isAuthenticated = true;
      StorageHelper.setCookie(AppConfig.STORAGE_KEYS.token, accessToken, {
        expires: new Date(expireDate)
      // maxAge: 60 * 60,
      });
    },
    clearAuthData: (auth) => {
      // console.log('clearAuthData: ', auth);
      auth.user = {};
      auth.isAuthenticated = false;
      auth.userInfoSidebar = {};
      StorageHelper.removeCookie(AppConfig.STORAGE_KEYS.token);
    },
    updateOrganizationName: (auth, action) => {
      const { organizationName } = action.payload;
      auth.user["organizationName"] = organizationName;
    },
    updateFirstLastName: (auth, action) => {
      const { firstName, lastName } = action.payload;
      auth.user["firstName"] = firstName;
      auth.user["lastName"] = lastName;
    },
    updateUserInfoSidebar: (auth, action) => {
      const { firstName, lastName, organizationName } = action.payload;
      auth.userInfoSidebar.firstName = firstName;
      auth.userInfoSidebar.lastName = lastName;
      auth.userInfoSidebar.organizationName = organizationName;
    }
  },
  extraReducers: {
    [getMe.fulfilled]: (state, action) => {
      state.user = action.payload || {};
      state.isAuthenticated = action.payload ? true : false;
    },
    [getMe.rejected]: (state) => {
      state.user = {};
      state.userInfoSidebar = {};
      state.isAuthenticated = false;
    },
    [logout.fulfilled]: (state) => {
      state.user = {};
      state.isAuthenticated = false;
      state.userInfoSidebar = {};

    }
  },
});

const { reducer, actions } = session;
export const { clearAuthData, saveAuthToken, updateOrganizationName, updateUserInfoSidebar, updateFirstLastName } = actions;
export default reducer;

export const getAuthInfo = createSelector(
  (state) =>
    state.auth.user ||
    StorageHelper.getLocalObject(AppConfig.STORAGE_KEYS.user) || {},
  (authInfo) => authInfo
);
export const getOrganizationName = createSelector(
  (state) =>
      state.auth.user ? state.auth.user["organizationName"] : "",
  (authInfo) => authInfo
);

export const isLoggedIn = createSelector(
  (state) => state.auth.isAuthenticated,
  (data) => data
);

export const getUserInfoSidebar = createSelector(
  (state) => state.auth.userInfoSidebar ||  StorageHelper.getLocalObject(AppConfig.STORAGE_KEYS.user),
  (userInfoSidebar) => userInfoSidebar
);